import { Typography, Box, Button } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import * as types from 'application/_types/types.type';
import PinListItem from './styledComponents/PinListItem';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import PinSettingsForm from './configurationForms/PinSettingsForm';
import { API, graphqlOperation } from 'aws-amplify';
import useOrgStore from 'application/store/zustand/useOrgStore';
import {
  updateStageDisposition,
  createStageDisposition,
  updateStageCategory,
} from 'graphql/mutations';
import { GamifyToast } from 'application/common/CustomToasts';
import ConfirmationDialog from 'application/components/ConfirmationDialog';
import { GamifyIcon } from 'application/components/GamifyIcon';
const { v4: uuidv4 } = require('uuid');

interface PinsConfigProps {
  selectedCRM: types.StageCategoryPartial | null;
}

const newEmptyPin = {
  id: '',
  title: '',
  iconName: '',
  isSetupDeal: false,
  noRecording: false,
};
export default function PinsConfig({ selectedCRM }: PinsConfigProps) {
  const [selectedPin, setSelectedPin] =
    useState<types.StageDispositionPartial | null>(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const categories = useOrgStore((state) => state.categories);
  const setCategories = useOrgStore((state) => state.setCategories);
  // Keep selected pin in sync with incoming CRM
  useEffect(() => {
    if (!selectedCRM) return;
    const list = (selectedCRM.dispositions ??
      []) as types.StageDispositionPartial[];
    if (!selectedPin) {
      setSelectedPin(list[0]);
    }
  }, [selectedCRM?.id]);

  const computeReordered = useCallback(
    (
      list: types.StageDispositionPartial[],
      startIndex: number,
      endIndex: number
    ) => {
      const result = Array.from(list);
      const [moved] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, moved);
      return result;
    },
    []
  );

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      const { destination, source } = result;

      const dispositions = (selectedCRM?.dispositions ??
        []) as types.StageDispositionPartial[];

      if (!destination || destination.index === source.index) {
        return;
      }

      // 1) compute new list order locally
      const reordered = computeReordered(
        dispositions,
        source.index,
        destination.index
      );

      // 2) extract ids as the new dispositionOrder array
      const nextOrder = reordered.map((d) => String(d.id));

      // 3) optimistic UI: update the category in the store
      setCategories(
        categories.map((c) =>
          c.id === selectedCRM!.id
            ? {
                ...c,
                dispositions: reordered,
                flexAttributes: {
                  ...(c.flexAttributes ||
                    (selectedCRM as any).flexAttributes ||
                    {}),
                  dispositionOrder: nextOrder,
                },
              }
            : c
        )
      );

      // 4) persist on the backend
      try {
        await API.graphql(
          graphqlOperation(updateStageCategory, {
            input: {
              id: selectedCRM!.id,
              orgID: selectedCRM!.orgID,
              flexAttributes: JSON.stringify({
                ...((selectedCRM as any).flexAttributes || {}),
                dispositionOrder: nextOrder,
              }),
            },
          })
        );
        GamifyToast.success('Pins reordered');
      } catch (err) {
        console.error('[Pins DnD] Persist error', err);
        GamifyToast.error('Failed to reorder pins');
        // optional: revert the optimistic update if you keep a pre-change snapshot
      }
    },
    [categories, setCategories, selectedCRM, computeReordered]
  );

  const handlePinSubmit = async (values: types.StageDispositionPartial) => {
    if (values.id) {
      try {
        await API.graphql(
          graphqlOperation(updateStageDisposition, { input: values })
        );
        const categoryUpdated = categories.find(
          (category) => category.id === selectedCRM?.id
        );
        const dispositionsUpdated = categoryUpdated?.dispositions;
        const currentDisposition = dispositionsUpdated?.map(
          (disposition) => disposition.id === values.id
        );
        const updatedDisposition = { ...currentDisposition, ...values };
        const updatedDispositions = dispositionsUpdated?.map((disposition) =>
          disposition.id === values.id ? updatedDisposition : disposition
        );
        const updatedCategory = {
          ...categoryUpdated,
          dispositions: updatedDispositions,
        };
        const updatedCategories = categories.map((category) =>
          category.id === selectedCRM?.id ? updatedCategory : category
        );
        setCategories(updatedCategories);
        GamifyToast.success('Pin updated successfully');
      } catch (error) {
        GamifyToast.error('Error updating pin');
      }
    } else {
      // find the highest order
      // @ts-ignore
      const nextOrder = (() => {
        const list = selectedCRM?.dispositions ?? [];
        // Reduce while ignoring non-finite orders
        const max = list.reduce((acc, d) => {
          const n =
            typeof d.order === 'number' && Number.isFinite(d.order)
              ? d.order
              : -1;
          return n > acc ? n : acc;
        }, -1);
        return max + 1; // if none found -> 0
      })();

      const newPin = {
        ...values,
        id: uuidv4(),
        isDeleted: false,
        isDefault: false,
        requireDate: false,
        categoryID: selectedCRM?.id,
        orgID: selectedCRM?.orgID,
        order: nextOrder,
        isNonMapDefault: false,
        deleteType: 'always',
      };
      try {
        await API.graphql(
          graphqlOperation(createStageDisposition, { input: newPin })
        );
        const categoryUpdated = categories.find(
          (category) => category.id === selectedCRM?.id
        );
        const dispositionsUpdated = categoryUpdated?.dispositions;
        // @ts-ignore
        const updatedDispositions = [...dispositionsUpdated, newPin];
        const updatedCategory = {
          ...categoryUpdated,
          dispositions: updatedDispositions,
        };
        const updatedCategories = categories.map((category) =>
          category.id === selectedCRM?.id ? updatedCategory : category
        );
        setCategories(updatedCategories);
        GamifyToast.success('Pin created successfully');
        setSelectedPin(newPin as types.StageDispositionPartial);
      } catch (error) {
        console.error('Error creating pin', error);
        GamifyToast.error('Error creating pin');
      }
    }
  };

  const handleAddPin = () => {
    setSelectedPin(newEmptyPin);
  };
  const handleDeletePin = async () => {
    try {
      await API.graphql(
        graphqlOperation(updateStageDisposition, {
          input: { id: selectedPin?.id, isDeleted: true },
        })
      );
      const updatedCategory = categories.find(
        (category) => category.id === selectedCRM?.id
      );
      const newPinsOrder =
        updatedCategory!.flexAttributes.dispositionOrder.filter(
          (id: string) => id !== selectedPin?.id
        );
      updatedCategory!.flexAttributes.dispositionOrder = newPinsOrder;
      await API.graphql(
        graphqlOperation(updateStageCategory, {
          input: {
            id: selectedCRM?.id,
            flexAttributes: JSON.stringify(updatedCategory!.flexAttributes),
          },
        })
      );

      const updatedDispositions = updatedCategory?.dispositions?.filter(
        (disposition) => disposition.id !== selectedPin?.id
      );
      const updatedCategories = categories.map((category) =>
        category.id === selectedCRM?.id
          ? { ...updatedCategory, dispositions: updatedDispositions }
          : category
      );
      setCategories(updatedCategories as any);
      setSelectedPin(
        updatedCategory?.dispositions?.[0] as types.StageDispositionPartial
      );
      setConfirmationDialogOpen(false);
      GamifyToast.success('Pin deleted successfully');
    } catch (error) {
      console.error('Error deleting pin', error);
      GamifyToast.error('Error deleting pin');
    }
  };

  if (!selectedCRM) return null;
  const dispositions = (selectedCRM.dispositions ??
    []) as types.StageDispositionPartial[];

  return (
    <Box>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography
          sx={(theme) => ({
            fontWeight: 780,
            fontSize: 18,
            color: theme.custom?.text,
          })}
        >
          Customize Pins
        </Typography>
        <Typography
          sx={(theme) => ({
            fontWeight: 400,
            fontSize: 15,
            color: theme.custom?.textLight,
          })}
        >
          Personalize and reorder your pins according to your sales needs.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row' }}>
        {/* Left: Pins list with DnD (renders from selectedCRM.dispositions) */}
        <Box
          sx={(theme) => ({
            background: theme.custom?.isDark
              ? 'rgba(53, 130, 158, 0.15)'
              : 'rgba(201, 231, 238, 0.50)',
            border: `2px dashed ${theme.custom?.secondary}`,
            borderRadius: '14px',
            p: '16px 20px 24px 20px',
            width: '30%',
            minWidth: 280,
          })}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={(theme) => ({
                fontSize: 18,
                fontWeight: 700,
                color: theme.custom?.text,
              })}
            >
              {`Pins (${dispositions.length})`}
            </Typography>
            <Button
              variant="contained"
              sx={(theme) => ({
                backgroundColor: theme.custom?.text,
                color: theme.custom?.background1,
                '&:hover': {
                  backgroundColor: theme.custom?.text,
                  opacity: 0.9,
                },
              })}
              onClick={handleAddPin}
              startIcon={
                <GamifyIcon
                  icon="add"
                  backgroundColor="#03252E"
                  color="white"
                />
              }
            >
              Add pin
            </Button>
          </Box>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="pins">
              {(droppableProvided) => (
                <Box
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  {dispositions.map((disposition, index) => (
                    <Draggable
                      key={String(disposition.id)}
                      draggableId={String(disposition.id)}
                      index={index}
                    >
                      {(draggableProvided, draggableSnapshot) => (
                        <Box
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          onClick={() => setSelectedPin(disposition)}
                          sx={{
                            cursor: draggableSnapshot.isDragging
                              ? 'grabbing'
                              : 'grab',
                          }}
                        >
                          <PinListItem
                            disposition={disposition}
                            isSelected={selectedPin?.id === disposition.id}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        {/* Right: Settings form */}
        <Box sx={{ width: '70%' }}>
          {selectedPin && (
            <PinSettingsForm
              selectedCRM={selectedCRM}
              disposition={selectedPin as types.StageDispositionPartial}
              onSubmit={handlePinSubmit}
              onDelete={() => {
                setConfirmationDialogOpen(true);
              }}
            />
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleDeletePin}
        title={`Are you sure you want to delete the "${selectedPin?.title} pin"? This action cannot be undone.`}
      />
    </Box>
  );
}
